import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Badge, Modal, Tabs, Tab, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumberWithCommas } from '../utilities';

// Receive Rolls: the one way paper rolls come into stock (replaces Roll Purchase).
// One receipt per truck/challan, against a PO (supplier + price from the PO) or "No PO".
// Each weight typed becomes one roll. Receipts can be fixed afterwards; the server keeps
// stock and the PO's received qty in step and logs every change.

const BANGLA_DIGITS = '০১২৩৪৫৬৭৮৯';
const toLatin = (s) => String(s || '').replace(/[০-৯]/g, (d) => String(BANGLA_DIGITS.indexOf(d)));
const parseWeights = (text) => {
    const weights = [];
    const bad = [];
    toLatin(text).split(/[\s,+]+/).filter(Boolean).forEach((t) => {
        const n = Number(t);
        if (Number.isFinite(n) && n > 0 && n <= 5000) weights.push(Math.round(n * 100) / 100);
        else bad.push(t);
    });
    return { weights, bad };
};
const num = (s) => { const n = Number(toLatin(s).trim()); return Number.isFinite(n) ? n : NaN; };
const kgText = (n) => `${formatNumberWithCommas(Math.round(Number(n) || 0))} kg`;
const todayBd = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);
const paperText = (p) => (p ? `${p.Name || p.GSMName || ''} ${p.GSMValue || ''}`.trim() : '');
const newRequestId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const emptyGroup = (gsmId = '') => ({ gsmId, size: '', weights: '' });
const emptyForm = (purchaseId = '') => ({
    purchaseId, noPo: false, vendorId: '', supplierName: '', challanNo: '', truckNo: '',
    receivedDate: todayBd(), groups: [emptyGroup()], requestId: newRequestId(),
});
const poRate = (po, gsmId, size) => {
    if (!po || !gsmId) return null;
    const paper = po.lines.filter((l) => Number(l.GSMID) === Number(gsmId));
    const exact = paper.find((l) => Math.abs(Number(l.WidthInch) - size) < 0.001);
    const pick = exact || paper[0];
    return pick && Number(pick.UnitPrice) > 0 ? Number(pick.UnitPrice) : null;
};
const apiError = (err, fallback) => err?.response?.data?.error || fallback;

function RollReceipts() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get('purchaseId') ? 'receive' : 'list');
    const [options, setOptions] = useState({ purchases: [], vendors: [], papers: [] });
    const [openId, setOpenId] = useState(searchParams.get('receiptId') || null);
    const [listKey, setListKey] = useState(0);

    const loadOptions = useCallback(() => {
        axios.get(`${window.backendUrl}/roll_receiving/options`)
            .then((res) => setOptions(res.data))
            .catch((err) => console.error('Error loading receiving options', err));
    }, []);
    useEffect(() => { loadOptions(); }, [loadOptions]);

    const openReceipt = (id) => {
        setOpenId(id);
        const p = new URLSearchParams(searchParams);
        if (id) p.set('receiptId', id); else p.delete('receiptId');
        setSearchParams(p, { replace: true });
    };

    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-3">
                    <Col md={11}>
                        <h4>Receive Rolls</h4>
                        <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
                            <Tab eventKey="receive" title="Receive a truck">
                                <ReceiveForm
                                    options={options}
                                    initialPurchaseId={searchParams.get('purchaseId') || ''}
                                    onSaved={(id) => { loadOptions(); setListKey((k) => k + 1); openReceipt(id); }}
                                />
                            </Tab>
                            <Tab eventKey="list" title="Receipts">
                                <ReceiptList key={listKey} vendors={options.vendors} onOpen={openReceipt} />
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>
            {openId && (
                <ReceiptDetail
                    id={openId}
                    options={options}
                    onClose={() => openReceipt(null)}
                    onChanged={() => { setListKey((k) => k + 1); loadOptions(); }}
                    onPrint={(id) => navigate(`/roll-codes?receiptId=${id}`)}
                />
            )}
        </>
    );
}

// ------------------------------------------------------------------ Receive form
function ReceiveForm({ options, initialPurchaseId, onSaved }) {
    const [form, setForm] = useState(() => emptyForm(initialPurchaseId ? Number(initialPurchaseId) : ''));
    const [saving, setSaving] = useState(false);
    const po = options.purchases.find((p) => p.PurchaseID === Number(form.purchaseId)) || null;
    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setGroup = (i, patch) => setForm((f) => ({ ...f, groups: f.groups.map((g, j) => (j === i ? { ...g, ...patch } : g)) }));

    // A PO with one paper fills the paper of empty groups
    useEffect(() => {
        if (!po) return;
        const papers = [...new Set(po.lines.map((l) => l.GSMID))];
        if (papers.length === 1) setForm((f) => ({ ...f, groups: f.groups.map((g) => (g.gsmId ? g : { ...g, gsmId: papers[0] })) }));
    }, [po]);

    const parsed = useMemo(() => form.groups.map((g) => {
        const { weights, bad } = parseWeights(g.weights);
        const size = num(g.size);
        return {
            weights, bad, size,
            kg: weights.reduce((s, w) => s + w, 0),
            rate: poRate(po, g.gsmId, size),
            onPo: !!(po && g.gsmId && po.lines.some((l) => Number(l.GSMID) === Number(g.gsmId) && Math.abs(Number(l.WidthInch) - size) < 0.001)),
        };
    }), [form.groups, po]);
    const totalRolls = parsed.reduce((s, p) => s + p.weights.length, 0);
    const totalKg = parsed.reduce((s, p) => s + p.kg, 0);
    const vendor = options.vendors.find((v) => v.VendorID === Number(form.vendorId));
    const supplier = po ? po.VendorName : vendor ? vendor.Name : form.supplierName;

    const problem = () => {
        if (!form.purchaseId && !form.noPo) return 'Pick the PO, or "No PO".';
        if (form.noPo && !form.vendorId && !form.supplierName.trim()) return 'Pick or type the supplier.';
        if (!form.receivedDate || form.receivedDate > todayBd()) return 'Pick a date (not in the future).';
        for (let i = 0; i < form.groups.length; i++) {
            const g = form.groups[i];
            const p = parsed[i];
            if (!g.gsmId && !g.size && !g.weights.trim() && form.groups.length > 1) continue;
            if (!g.gsmId) return `Row ${i + 1}: pick the paper.`;
            if (!(p.size > 0 && p.size < 300)) return `Row ${i + 1}: type the size.`;
            if (p.bad.length) return `Row ${i + 1}: "${p.bad.join(' ')}" is not a weight.`;
            if (!p.weights.length) return `Row ${i + 1}: type the weights.`;
        }
        return totalRolls ? null : 'Type at least one roll weight.';
    };

    const save = async (confirmDuplicate = false) => {
        if (!confirmDuplicate) {
            const p = problem();
            if (p) return alert(p);
            if (!window.confirm(`Receive ${totalRolls} rolls, ${kgText(totalKg)} from ${supplier}${po ? ` (${po.PONo})` : ' (No PO)'}?\n\nCheck this matches the challan total.`)) return;
        }
        setSaving(true);
        try {
            const res = await axios.post(`${window.backendUrl}/roll_receipts`, {
                clientRequestId: form.requestId,
                purchaseId: form.purchaseId || null,
                vendorId: form.noPo && form.vendorId ? Number(form.vendorId) : null,
                supplierName: form.noPo && !form.vendorId ? form.supplierName.trim() : null,
                challanNo: form.challanNo, truckNo: form.truckNo, receivedDate: form.receivedDate,
                groups: form.groups.map((g, i) => ({ gsmId: Number(g.gsmId), rollSize: parsed[i].size, weights: parsed[i].weights }))
                    .filter((g) => g.gsmId && g.weights.length),
                confirmDuplicate,
            });
            setForm(emptyForm());
            onSaved(res.data.receiptId);
        } catch (err) {
            if (err?.response?.status === 409 && err.response.data?.duplicate) {
                if (window.confirm(err.response.data.error)) { setSaving(false); return save(true); }
            } else {
                alert(apiError(err, 'Failed to save'));
            }
        }
        setSaving(false);
    };

    const papersSorted = useMemo(() => {
        if (!po) return options.papers;
        const onPo = new Set(po.lines.map((l) => l.GSMID));
        return [...options.papers.filter((p) => onPo.has(p.GSMID)), ...options.papers.filter((p) => !onPo.has(p.GSMID))];
    }, [options.papers, po]);

    return (
        <div style={{ maxWidth: 1000 }}>
            <Row className="g-2 mb-2">
                <Col md={5}>
                    <Form.Label className="fw-bold mb-1">PO</Form.Label>
                    <Form.Select size="sm" value={form.noPo ? 'none' : form.purchaseId}
                        onChange={(e) => (e.target.value === 'none'
                            ? set({ noPo: true, purchaseId: '' })
                            : set({ noPo: false, purchaseId: e.target.value ? Number(e.target.value) : '', vendorId: '', supplierName: '' }))}>
                        <option value="">Pick the PO…</option>
                        <option value="none">No PO (the office has not made one yet)</option>
                        {options.purchases.map((p) => (
                            <option key={p.PurchaseID} value={p.PurchaseID}>{p.VendorName} – {p.PONo}</option>
                        ))}
                    </Form.Select>
                </Col>
                {form.noPo && (
                    <Col md={4}>
                        <Form.Label className="fw-bold mb-1">Supplier</Form.Label>
                        <Form.Select size="sm" value={form.vendorId} onChange={(e) => set({ vendorId: e.target.value, supplierName: '' })}>
                            <option value="">Not in the list (type below)</option>
                            {options.vendors.map((v) => <option key={v.VendorID} value={v.VendorID}>{v.Name}</option>)}
                        </Form.Select>
                        {!form.vendorId && (
                            <Form.Control size="sm" className="mt-1" placeholder="Supplier name" value={form.supplierName}
                                onChange={(e) => set({ supplierName: e.target.value })} />
                        )}
                    </Col>
                )}
                <Col md={form.noPo ? 3 : 7}>
                    <Row className="g-2">
                        <Col><Form.Label className="fw-bold mb-1">Challan no.</Form.Label>
                            <Form.Control size="sm" value={form.challanNo} onChange={(e) => set({ challanNo: e.target.value })} /></Col>
                        <Col><Form.Label className="fw-bold mb-1">Truck no.</Form.Label>
                            <Form.Control size="sm" value={form.truckNo} onChange={(e) => set({ truckNo: e.target.value })} /></Col>
                        <Col><Form.Label className="fw-bold mb-1">Date</Form.Label>
                            <Form.Control size="sm" type="date" max={todayBd()} value={form.receivedDate} onChange={(e) => set({ receivedDate: e.target.value })} /></Col>
                    </Row>
                </Col>
            </Row>

            {po && (
                <div className="mb-2" style={{ fontSize: 12, color: '#555' }}>
                    {po.lines.map((l) => (
                        <span key={l.LineID} className="me-3">{paperText(l)} {l.WidthInch}": {Number(l.ReceivedQty)}/{Number(l.Qty)} {l.Unit} received · {Number(l.UnitPrice)}/kg</span>
                    ))}
                </div>
            )}
            {form.noPo && <Alert variant="warning" className="py-1 px-2" style={{ fontSize: 13 }}>No PO: these rolls go into stock without a price. Attach the PO or set the price later from Receipts.</Alert>}

            <Table size="sm" bordered style={{ backgroundColor: 'white', fontSize: 13 }}>
                <thead style={{ backgroundColor: '#d1e7dd' }}>
                    <tr><th style={{ width: '25%' }}>Paper</th><th style={{ width: '10%' }}>Size (inch)</th><th>Weights (kg), one per roll — e.g. 808 819 772 749 832</th><th style={{ width: '15%' }}>Rolls · kg</th><th style={{ width: '3%' }}></th></tr>
                </thead>
                <tbody>
                    {form.groups.map((g, i) => {
                        const p = parsed[i];
                        return (
                            <tr key={i}>
                                <td>
                                    <Form.Select size="sm" value={g.gsmId} onChange={(e) => setGroup(i, { gsmId: e.target.value ? Number(e.target.value) : '' })}>
                                        <option value="">Pick the paper</option>
                                        {papersSorted.map((pp) => <option key={pp.GSMID} value={pp.GSMID}>{paperText(pp)}</option>)}
                                    </Form.Select>
                                </td>
                                <td><Form.Control size="sm" value={g.size} onChange={(e) => setGroup(i, { size: e.target.value })} /></td>
                                <td>
                                    <Form.Control size="sm" as="textarea" rows={1} value={g.weights} onChange={(e) => setGroup(i, { weights: e.target.value })} />
                                    {p.bad.length > 0 && <div className="text-danger" style={{ fontSize: 12 }}>Not a weight: {p.bad.join(' ')}</div>}
                                    {po && g.gsmId && p.size > 0 && (
                                        <div style={{ fontSize: 12, color: p.onPo ? '#666' : '#b02a37' }}>
                                            {p.rate ? `${p.rate}/kg` : 'No price on the PO'}{!p.onPo ? ' · size not on the PO' : ''}
                                        </div>
                                    )}
                                </td>
                                <td style={{ verticalAlign: 'middle', fontWeight: 'bold' }}>{p.weights.length} · {kgText(p.kg)}</td>
                                <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                    {form.groups.length > 1 && <i className="fa fa-trash text-danger" style={{ cursor: 'pointer' }}
                                        onClick={() => set({ groups: form.groups.filter((_, j) => j !== i) })}></i>}
                                </td>
                            </tr>
                        );
                    })}
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <td colSpan={3}>
                            <Button size="sm" variant="outline-primary"
                                onClick={() => set({ groups: [...form.groups, emptyGroup(form.groups[form.groups.length - 1]?.gsmId || '')] })}>+ Add another size</Button>
                        </td>
                        <td colSpan={2} style={{ fontWeight: 'bold', fontSize: 15 }}>{totalRolls} rolls · {kgText(totalKg)}</td>
                    </tr>
                </tbody>
            </Table>
            <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <Button variant="success" disabled={saving} onClick={() => save(false)}>{saving ? 'Saving…' : 'Receive'}</Button>
                <span className="text-muted" style={{ fontSize: 13 }}>Check the total matches the challan before saving.</span>
                <Button size="sm" variant="link" className="ms-auto text-danger" onClick={() => window.confirm('Clear the form?') && setForm(emptyForm())}>Clear</Button>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------ Receipt list
function ReceiptList({ vendors, onOpen }) {
    const [filters, setFilters] = useState({ from: '', to: '', vendorId: '', noPo: false, noPrice: false, status: '', q: '' });
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ data: [], totalPages: 1, totalRows: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoading(true);
            axios.get(`${window.backendUrl}/roll_receipts`, {
                params: {
                    page, from: filters.from || undefined, to: filters.to || undefined, vendorId: filters.vendorId || undefined,
                    noPo: filters.noPo ? '1' : undefined, noPrice: filters.noPrice ? '1' : undefined,
                    status: filters.status || undefined, q: filters.q.trim() || undefined,
                },
            })
                .then((res) => setData(res.data))
                .catch((err) => console.error('Error loading receipts', err))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(timer);
    }, [filters, page]);

    const setF = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
    const cell = { padding: '4px 6px', verticalAlign: 'middle', fontSize: 12 };
    const head = { ...cell, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap', textAlign: 'center' };

    return (
        <>
            <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: 8 }}>
                <Form.Control size="sm" type="date" style={{ width: 150 }} value={filters.from} onChange={(e) => setF({ from: e.target.value })} title="From" />
                <Form.Control size="sm" type="date" style={{ width: 150 }} value={filters.to} onChange={(e) => setF({ to: e.target.value })} title="To" />
                <Form.Select size="sm" style={{ width: 200 }} value={filters.vendorId} onChange={(e) => setF({ vendorId: e.target.value })}>
                    <option value="">All suppliers</option>
                    {vendors.map((v) => <option key={v.VendorID} value={v.VendorID}>{v.Name}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{ width: 130 }} value={filters.status} onChange={(e) => setF({ status: e.target.value })}>
                    <option value="">All</option><option value="Active">Active</option><option value="Cancelled">Cancelled</option>
                </Form.Select>
                <Form.Check type="checkbox" label="Without PO" checked={filters.noPo} onChange={(e) => setF({ noPo: e.target.checked })} />
                <Form.Check type="checkbox" label="Without price" checked={filters.noPrice} onChange={(e) => setF({ noPrice: e.target.checked })} />
                <Form.Control size="sm" style={{ width: 200 }} placeholder="Challan, truck, supplier, PO" value={filters.q} onChange={(e) => setF({ q: e.target.value })} />
                <span style={{ fontSize: 12, color: '#666' }}>{data.totalRows} receipts</span>
            </div>
            <Table bordered hover size="sm" style={{ backgroundColor: 'white' }}>
                <thead><tr>
                    <th style={head}>Receipt</th><th style={head}>Date</th><th style={head}>Supplier</th><th style={head}>PO</th>
                    <th style={head}>Challan</th><th style={head}>Truck</th><th style={head}>Rolls</th><th style={head}>Kg</th>
                    <th style={head}>Value</th><th style={head}>Needs attention</th><th style={head}>Received by</th>
                </tr></thead>
                <tbody>
                    {loading ? <tr><td colSpan={11} className="text-center p-3">Loading...</td></tr>
                        : data.data.length === 0 ? <tr><td colSpan={11} className="text-center p-3">No receipts</td></tr>
                            : data.data.map((r) => (
                                <tr key={r.ReceiptID} style={{ cursor: 'pointer', opacity: r.Status === 'Cancelled' ? 0.5 : 1 }} onClick={() => onOpen(r.ReceiptID)}>
                                    <td style={{ ...cell, fontWeight: 'bold' }}>{r.ReceiptNo}</td>
                                    <td style={cell}>{r.ReceivedDateText}</td>
                                    <td style={cell}>{r.Supplier}</td>
                                    <td style={cell}>{r.PONo || '—'}</td>
                                    <td style={cell}>{r.ChallanNo || ''}</td>
                                    <td style={cell}>{r.TruckNo || ''}</td>
                                    <td style={{ ...cell, textAlign: 'right' }}>{r.Rolls}</td>
                                    <td style={{ ...cell, textAlign: 'right' }}>{kgText(r.Kg)}</td>
                                    <td style={{ ...cell, textAlign: 'right' }}>৳{formatNumberWithCommas(Number(r.Value).toFixed(0))}</td>
                                    <td style={cell}>
                                        {r.Status === 'Cancelled' && <Badge bg="dark" className="me-1">Cancelled</Badge>}
                                        {r.Status === 'Active' && !r.PurchaseID && <Badge bg="warning" text="dark" className="me-1">No PO</Badge>}
                                        {r.Status === 'Active' && r.NoPriceRolls > 0 && <Badge bg="danger" className="me-1">No price ({r.NoPriceRolls})</Badge>}
                                        {r.ReprintRolls > 0 && <Badge bg="info" className="me-1">Reprint ({r.ReprintRolls})</Badge>}
                                        {r.ReturnedRolls > 0 && <Badge bg="secondary" className="me-1">Returned ({r.ReturnedRolls})</Badge>}
                                    </td>
                                    <td style={cell}>{r.CreatedByName || ''}</td>
                                </tr>
                            ))}
                </tbody>
            </Table>
            {data.totalPages > 1 && (
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                    <span style={{ fontSize: 13 }}>Page {page} of {data.totalPages}</span>
                    <Button size="sm" variant="outline-secondary" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
            )}
        </>
    );
}

// ------------------------------------------------------------------ One receipt
function ReceiptDetail({ id, options, onClose, onChanged, onPrint }) {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    const [head, setHead] = useState(null);
    const [edits, setEdits] = useState({}); // rollId -> { weight, size, gsmId }
    const [add, setAdd] = useState({ gsmId: '', size: '', weights: '' });
    const [poPick, setPoPick] = useState({ purchaseId: '', applyPoPrice: true });
    const [rates, setRates] = useState({}); // "gsmId|size" -> text
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/roll_receipts/${id}`);
            const r = res.data.receipt;
            setD(res.data);
            setHead({ challanNo: r.ChallanNo || '', truckNo: r.TruckNo || '', receivedDate: r.ReceivedDateText, note: r.Note || '', vendorId: r.VendorID || '', supplierName: r.SupplierName || '' });
            setEdits({});
            setRates({});
            setPoPick({ purchaseId: r.PurchaseID || '', applyPoPrice: true });
        } catch (err) {
            alert(apiError(err, 'Failed to load the receipt'));
            closeRef.current();
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, msg) => {
        setBusy(true);
        try {
            const res = await fn();
            await load();
            onChanged();
            if (msg || res?.data?.message) alert(msg || res.data.message);
        } catch (err) {
            alert(apiError(err, 'Failed'));
        }
        setBusy(false);
    };

    if (!d) return <Modal show size="xl" onHide={onClose}><Modal.Body>Loading...</Modal.Body></Modal>;

    const { receipt: r, rolls, log, permissions: can, poLines } = d;
    const live = rolls.filter((x) => x.Status !== 'Returned');
    const base = `${window.backendUrl}/roll_receipts/${r.ReceiptID}`;
    const isActive = r.Status === 'Active';
    // Groups of paper+size for the price editor
    const groups = [];
    rolls.forEach((x) => {
        const key = `${x.GSMID}|${x.RollSize}`;
        let g = groups.find((y) => y.key === key);
        if (!g) { g = { key, gsmId: x.GSMID, size: x.RollSize, paper: x.PaperName, rates: new Set(), n: 0 }; groups.push(g); }
        g.rates.add(x.RatePerKg == null ? 'none' : x.RatePerKg);
        g.n++;
    });
    const editOf = (x) => edits[x.RollID] || { weight: String(x.InitialWeight), size: String(x.RollSize), gsmId: x.GSMID };
    const setEdit = (x, patch) => setEdits((e) => ({ ...e, [x.RollID]: { ...editOf(x), ...patch } }));
    const rowChanged = (x) => {
        const e = edits[x.RollID];
        return e && (num(e.weight) !== Number(x.InitialWeight) || num(e.size) !== Number(x.RollSize) || Number(e.gsmId) !== x.GSMID);
    };
    const saveRow = (x) => {
        const e = editOf(x);
        const body = {};
        if (num(e.weight) !== Number(x.InitialWeight)) body.weight = num(e.weight);
        if (num(e.size) !== Number(x.RollSize)) body.rollSize = num(e.size);
        if (Number(e.gsmId) !== x.GSMID) body.gsmId = Number(e.gsmId);
        act(() => axios.put(`${base}/rolls/${x.RollID}`, body), body.rollSize || body.gsmId ? 'Saved. Print a new label for this roll.' : 'Saved.');
    };
    const otherPos = options.purchases;
    const cell = { padding: '3px 6px', verticalAlign: 'middle', fontSize: 12 };

    return (
        <Modal show size="xl" onHide={onClose} scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18 }}>
                    {r.ReceiptNo} · {r.Supplier} · {r.PONo || 'No PO'} {r.Status === 'Cancelled' && <Badge bg="dark">Cancelled</Badge>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-2" style={{ fontSize: 14 }}>
                    <strong>{live.length} rolls · {kgText(live.reduce((s, x) => s + Number(x.InitialWeight), 0))}</strong>
                    <span className="text-muted ms-3">Received by {r.CreatedByName || '—'} on {r.ReceivedDateText}</span>
                    {!can.canFix && isActive && <span className="text-muted ms-3">(Only an admin can change rolls now.)</span>}
                    <Button size="sm" variant="outline-success" className="ms-3" onClick={() => onPrint(r.ReceiptID)}>Print labels</Button>
                </div>

                {/* Header */}
                {head && (
                    <Row className="g-2 mb-3 align-items-end">
                        <Col md={2}><Form.Label className="mb-0" style={{ fontSize: 12 }}>Challan</Form.Label>
                            <Form.Control size="sm" disabled={!can.canFix} value={head.challanNo} onChange={(e) => setHead({ ...head, challanNo: e.target.value })} /></Col>
                        <Col md={2}><Form.Label className="mb-0" style={{ fontSize: 12 }}>Truck</Form.Label>
                            <Form.Control size="sm" disabled={!can.canFix} value={head.truckNo} onChange={(e) => setHead({ ...head, truckNo: e.target.value })} /></Col>
                        <Col md={2}><Form.Label className="mb-0" style={{ fontSize: 12 }}>Date</Form.Label>
                            <Form.Control size="sm" type="date" max={todayBd()} disabled={!can.canFix} value={head.receivedDate} onChange={(e) => setHead({ ...head, receivedDate: e.target.value })} /></Col>
                        {!r.PurchaseID && (
                            <Col md={3}><Form.Label className="mb-0" style={{ fontSize: 12 }}>Supplier</Form.Label>
                                <Form.Select size="sm" disabled={!can.canFix} value={head.vendorId} onChange={(e) => setHead({ ...head, vendorId: e.target.value })}>
                                    <option value="">{head.supplierName ? `Typed: ${head.supplierName}` : 'Typed name'}</option>
                                    {options.vendors.map((v) => <option key={v.VendorID} value={v.VendorID}>{v.Name}</option>)}
                                </Form.Select></Col>
                        )}
                        <Col md={2}><Form.Label className="mb-0" style={{ fontSize: 12 }}>Note</Form.Label>
                            <Form.Control size="sm" disabled={!can.canFix} value={head.note} onChange={(e) => setHead({ ...head, note: e.target.value })} /></Col>
                        {can.canFix && (
                            <Col md="auto">
                                <Button size="sm" disabled={busy} onClick={() => {
                                    const body = { challanNo: head.challanNo, truckNo: head.truckNo, receivedDate: head.receivedDate, note: head.note };
                                    if (!r.PurchaseID && String(head.vendorId || '') !== String(r.VendorID || '')) {
                                        if (head.vendorId) body.vendorId = Number(head.vendorId); else body.supplierName = head.supplierName || r.SupplierName;
                                    }
                                    act(() => axios.put(base, body));
                                }}>Save</Button>
                            </Col>
                        )}
                    </Row>
                )}

                {/* PO */}
                {can.canChangePo && (
                    <div className="d-flex flex-wrap align-items-center mb-3 p-2" style={{ gap: 8, background: '#f6f8fa', borderRadius: 6 }}>
                        <strong style={{ fontSize: 13 }}>PO:</strong>
                        <Form.Select size="sm" style={{ width: 320 }} value={poPick.purchaseId} onChange={(e) => setPoPick({ ...poPick, purchaseId: e.target.value ? Number(e.target.value) : '' })}>
                            <option value="">No PO</option>
                            {r.PurchaseID && !otherPos.some((p) => p.PurchaseID === r.PurchaseID) && <option value={r.PurchaseID}>{r.PONo}</option>}
                            {otherPos.map((p) => <option key={p.PurchaseID} value={p.PurchaseID}>{p.VendorName} – {p.PONo}</option>)}
                        </Form.Select>
                        <Form.Check type="checkbox" label="Use the PO's price for these rolls" checked={poPick.applyPoPrice} onChange={(e) => setPoPick({ ...poPick, applyPoPrice: e.target.checked })} />
                        <Button size="sm" variant="primary" disabled={busy || String(poPick.purchaseId || '') === String(r.PurchaseID || '')}
                            onClick={() => window.confirm(poPick.purchaseId ? 'Move this receipt to the chosen PO?' : 'Take this receipt off its PO?')
                                && act(() => axios.post(`${base}/purchase`, { purchaseId: poPick.purchaseId || null, applyPoPrice: poPick.applyPoPrice }))}>
                            {r.PurchaseID ? 'Change PO' : 'Attach PO'}
                        </Button>
                    </div>
                )}

                {/* Price */}
                {can.canChangePrice && (
                    <div className="mb-3">
                        <strong style={{ fontSize: 13 }}>Price per kg</strong>
                        <Table size="sm" bordered className="mt-1" style={{ width: 'auto' }}>
                            <tbody>
                                {groups.map((g) => (
                                    <tr key={g.key}>
                                        <td style={cell}>{g.paper} {g.size}" ({g.n} rolls)</td>
                                        <td style={cell}>now: {[...g.rates].join(' / ')}</td>
                                        <td style={cell}><Form.Control size="sm" style={{ width: 100 }} placeholder="new price" value={rates[g.key] || ''} onChange={(e) => setRates({ ...rates, [g.key]: e.target.value })} /></td>
                                        <td style={cell}><Button size="sm" variant="outline-primary" disabled={busy || !(num(rates[g.key] || '') > 0)}
                                            onClick={() => act(() => axios.post(`${base}/rate`, { rate: num(rates[g.key]), gsmId: g.gsmId, rollSize: g.size }))}>Set</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {poLines.length > 0 && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                                On {r.PONo}: {poLines.map((l) => `${l.PaperName} ${l.WidthInch}" ${Number(l.UnitPrice)}/kg (${Number(l.ReceivedQty)}/${Number(l.Qty)} ${l.Unit} received)`).join(' · ')}
                            </div>
                        )}
                    </div>
                )}

                {/* Rolls */}
                <Table bordered size="sm" style={{ backgroundColor: 'white' }}>
                    <thead style={{ backgroundColor: '#d1e7dd' }}><tr>
                        <th style={cell}>Roll code</th><th style={cell}>Paper</th><th style={cell}>Size</th><th style={cell}>Weight (kg)</th>
                        <th style={cell}>Left (kg)</th><th style={cell}>Price/kg</th><th style={cell}>Status</th><th style={cell}></th>
                    </tr></thead>
                    <tbody>
                        {rolls.map((x) => {
                            const e = editOf(x);
                            const editable = can.canFix && x.Status !== 'Returned';
                            return (
                                <tr key={x.RollID} style={{ opacity: x.Status === 'Returned' ? 0.55 : 1 }}>
                                    <td style={{ ...cell, fontWeight: 'bold' }}>{x.RollCode}</td>
                                    <td style={cell}>
                                        {editable && !x.used ? (
                                            <Form.Select size="sm" value={e.gsmId} onChange={(ev) => setEdit(x, { gsmId: Number(ev.target.value) })}>
                                                {options.papers.map((p) => <option key={p.GSMID} value={p.GSMID}>{paperText(p)}</option>)}
                                            </Form.Select>
                                        ) : x.PaperName}
                                    </td>
                                    <td style={cell}>{editable && !x.used
                                        ? <Form.Control size="sm" style={{ width: 70 }} value={e.size} onChange={(ev) => setEdit(x, { size: ev.target.value })} />
                                        : `${x.RollSize}"`}</td>
                                    <td style={cell}>{editable && x.Status === 'Active'
                                        ? <Form.Control size="sm" style={{ width: 90 }} value={e.weight} onChange={(ev) => setEdit(x, { weight: ev.target.value })} />
                                        : x.InitialWeight}</td>
                                    <td style={cell}>{x.CurrentWeight}</td>
                                    <td style={cell}>{x.RatePerKg ?? <span className="text-danger">none</span>}</td>
                                    <td style={cell}>
                                        {x.Status !== 'Active' && <Badge bg="secondary" className="me-1">{x.Status}</Badge>}
                                        {x.used && <Badge bg="primary" className="me-1">Used</Badge>}
                                        {!!x.NeedsReprint && <Badge bg="danger">Reprint label</Badge>}
                                    </td>
                                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                                        {editable && rowChanged(x) && <Button size="sm" className="me-1" disabled={busy} onClick={() => saveRow(x)}>Save</Button>}
                                        {editable && !x.used && (
                                            <>
                                                <Button size="sm" variant="outline-secondary" className="me-1" disabled={busy}
                                                    onClick={() => window.confirm(`Mark ${x.RollCode} returned to supplier? It leaves stock.`) && act(() => axios.post(`${base}/rolls/${x.RollID}/return`, {}))}>Returned</Button>
                                                <Button size="sm" variant="outline-danger" disabled={busy}
                                                    onClick={() => window.confirm(`Delete ${x.RollCode}? Only if it was typed by mistake.`) && act(() => axios.delete(`${base}/rolls/${x.RollID}`))}>Delete</Button>
                                            </>
                                        )}
                                        {can.canFix && x.Status === 'Returned' && (
                                            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => act(() => axios.post(`${base}/rolls/${x.RollID}/return`, { undo: true }))}>Undo return</Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                {/* Add rolls */}
                {can.canFix && (
                    <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
                        <strong style={{ fontSize: 13 }}>Add rolls:</strong>
                        <Form.Select size="sm" style={{ width: 200 }} value={add.gsmId} onChange={(e) => setAdd({ ...add, gsmId: e.target.value })}>
                            <option value="">Paper</option>
                            {options.papers.map((p) => <option key={p.GSMID} value={p.GSMID}>{paperText(p)}</option>)}
                        </Form.Select>
                        <Form.Control size="sm" style={{ width: 80 }} placeholder='Size "' value={add.size} onChange={(e) => setAdd({ ...add, size: e.target.value })} />
                        <Form.Control size="sm" style={{ width: 300 }} placeholder="Weights, e.g. 808 819" value={add.weights} onChange={(e) => setAdd({ ...add, weights: e.target.value })} />
                        <Button size="sm" variant="outline-success" disabled={busy} onClick={() => {
                            const { weights, bad } = parseWeights(add.weights);
                            if (!add.gsmId || !(num(add.size) > 0) || !weights.length || bad.length) return alert('Pick the paper, type the size and the weights.');
                            act(() => axios.post(`${base}/rolls`, { groups: [{ gsmId: Number(add.gsmId), rollSize: num(add.size), weights }] }))
                                .then(() => setAdd({ gsmId: add.gsmId, size: '', weights: '' }));
                        }}>Add</Button>
                    </div>
                )}

                {can.canCancel && (
                    <Button size="sm" variant="danger" className="mb-3" disabled={busy}
                        onClick={() => {
                            const reason = window.prompt(`Cancel ${r.ReceiptNo}? All ${rolls.length} rolls leave stock. Reason:`);
                            if (reason !== null) act(() => axios.post(`${base}/cancel`, { reason }));
                        }}>Cancel the whole receipt</Button>
                )}

                <h6 className="mt-2">History</h6>
                <div style={{ fontSize: 12, maxHeight: 200, overflowY: 'auto' }}>
                    {log.map((l) => (
                        <div key={l.LogID}>
                            {new Date((l.CreatedAt + 6 * 3600) * 1000).toISOString().slice(0, 16).replace('T', ' ')} · {l.UserName || 'someone'} · <strong>{l.Action}</strong>
                            {l.RollCode ? ` ${l.RollCode}` : ''}{l.OldValue ? `: ${l.OldValue}` : ''}{l.NewValue ? ` → ${l.NewValue}` : ''}
                        </div>
                    ))}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default RollReceipts;
